import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check } from 'lucide-react';
import { useNotification } from '@/context/NotificationContext';
import { useNavigate } from 'react-router-dom';

export const NotificationCenter: React.FC = () => {
  const { notifications, unreadCount, markAllAsRead, clearAll, markAsRead } = useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close notifications popover on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  const handleNotificationClick = (id: string, entityId?: string) => {
    markAsRead(id);
    if (entityId) {
      navigate(`/requests/${entityId}`);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle notifications"
        className="relative rounded-xl p-2 hover:bg-muted text-foreground-secondary hover:text-foreground transition-colors cursor-pointer"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 text-[9px] font-bold text-white px-1 shadow-sm animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2.5 w-80 sm:w-96 rounded-2xl border border-border bg-white dark:bg-[#121526] p-4 shadow-2xl z-[100] animate-in fade-in duration-150 font-heading">
          <div className="flex items-center justify-between pb-3 border-b border-border/80">
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-black text-foreground">Notifications</h4>
              {unreadCount > 0 && (
                <span className="rounded-md bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-black">
                  {unreadCount} New
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={markAllAsRead}
                title="Mark all as read"
                className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1 cursor-pointer"
              >
                <Check className="h-3 w-3" /> Read All
              </button>
              <button
                onClick={clearAll}
                title="Clear all notifications"
                className="text-[11px] font-bold text-rose-600 dark:text-rose-400 hover:underline ml-2 cursor-pointer"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="mt-2.5 space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-xs text-foreground-muted">
                No notifications present at this time.
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n.id, n.entityId)}
                  className={`text-xs p-3 rounded-xl border transition-all cursor-pointer ${
                    n.isRead
                      ? 'bg-muted/40 border-border/60 opacity-80 hover:opacity-100'
                      : 'bg-primary/5 border-primary/30 font-medium shadow-2xs hover:bg-primary/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-bold text-foreground">{n.title}</p>
                    <span className="text-[10px] text-foreground-muted whitespace-nowrap font-mono">Recent</span>
                  </div>
                  <p className="text-[11px] text-foreground-secondary mt-1 leading-relaxed">{n.message}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
